'use client';

import { Modal, useScreenDetect } from '@sgcmpteltd/uikit-next';
import { Checkbox, IosArrowRight, StarRating } from '@sgcmpteltd/uikit-react';
import {
  Result,
  RESULT_VARIANT_WARNING,
} from '@sgcmpteltd/uikit-react/components/result';
import { isRatingValue } from '@sgcmpteltd/uikit-react/components/star-rating';
import cx from 'clsx';
import Cookies from 'js-cookie';
import Image from 'next/image';
import Link from 'next/link';
import type { MouseEventHandler } from 'react';
import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type {
  CamelCaseAdAlert,
  CamelCaseCarAlert,
} from '@/apis/server/new-car/v1/my-alert';
import type {
  CamelCasePromotion,
} from '@/apis/server/new-car/v1/promotions/types';
import {
  PromotionDetailsModal,
} from '@/app/_components/page_content/new-car-promo/promotion-details-modal';
import type { User } from '@/app/types';
import { ImageWithFallback } from '@/components/image-with-fallback/component';
import { DepreciationModal } from '@/components/modals/depreciation-modal';
import { TextLink } from '@/components/text-link';
import { Tooltip } from '@/components/tooltip';
import {
  COOKIE_KEY_MAIL,
  COOKIE_KEY_NON_LOGIN_SHORTLIST,
  COOKIE_KEY_SESSION_ID,
  COOKIE_KEY_USER_ID,
} from '@/configs/cookies';
import { SGCM_SITE_DOMAIN } from '@/configs/public-url';
import { SITE_BASE_PATH } from '@/configs/site';
import { UserContext } from '@/context/user';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';
import { createTimestampUid } from '@/utils/id';
import { getFormattedDateString } from '@/utils/string/formattedDate';
import {
  joinSgcmMedia,
  joinSgcmMobileSite,
  joinSgcmMsSite,
  joinSgcmSite,
} from '@/utils/url/join/public';

import type {
  AdAlertModalConent,
  CarAlertModalConent,
} from '../../alert-modal';
import { AlertModal } from '../../alert-modal';
import {
  ARROW_ICON_SIZE,
  ARROW_ICON_SIZE_IN_TOOLTIP,
  LIST_TYPE_LABEL_SIZE,
  NUM_NON_LOGIN_SHORTLIST_MAX_VEHICLES,
  SHORTLIST_CHECKBOX_SIZE,
  STAR_RATING_ICON_SIZE_DEFAULT,
} from './constants';
import { ListTypeLabel } from './listing-type-label';
import styles from './styles.module.scss';
import type { Component } from './types';
import { getFormattedLaunchDateString, getPriceRangeText } from './utils';

const isBrowser = () => typeof window !== 'undefined';

export const CarCard: Component = ({
  id,
  make,
  model,
  country,
  image,
  review,
  additionalProperties,
  submodelList,
  adList,
  alertList,
  hasPromotion,
  launchDate,
  compareItems,
  isPriority,
  promotionList,
  onAlertModalSubmit,
  onComparedCheckboxChange,
}) => {
  const { isDesktop } = useScreenDetect();
  const isDesktopMode = isDesktop();
  const userContext = useContext(UserContext);
  const user: User = {
    email: Cookies.get(COOKIE_KEY_MAIL) || '',
    userId: Number(Cookies.get(COOKIE_KEY_USER_ID)) || 0,
    sessionId: Cookies.get(COOKIE_KEY_SESSION_ID) || '',
  };

  const isPiModel = adList === null;
  const isComparedSubmodelArray = useMemo(() => {
    const comparedSubmodelArray = submodelList.map(
      submodel => compareItems.some(
        compareItem => compareItem.submodelId === submodel.id,
      ),
    );

    return comparedSubmodelArray;
  }, [compareItems, submodelList]);

  const [isShortlisted, setIsShortlisted] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [
    isAlertModalOpen,
    setIsAlertModalOpen,
  ] = useState(false);
  const [
    isDepreciationModalOpen,
    setIsDepreciationModalOpen,
  ] = useState(false);
  const [
    promotionDetailsModalKey,
    setPromotionDetailsModalKey,
  ] = useState(createTimestampUid);
  const [
    isPromotionDetailsModalOpen,
    setIsPromotionDetailsModalOpen,
  ] = useState(false);
  const [
    activePromotionListPromoId,
    setActivePromotionListPromoId,
  ] = useState<number | null>(null);

  const { adAlert, carAlert } = alertList;
  const { has360, hasVideo, thumbnailUrl } = image;
  const {
    cotyDescription,
    isCotyNominee,
    isNewModel,
    isSpotlight,
    isUpcoming,
  } = additionalProperties;
  const { count, averageRating } = review || {};
  const ratingValue = isRatingValue(averageRating) ? averageRating : null;

  const vehicleFullName = `${make} ${model}`;
  const formattedLaunchDate = launchDate
    && getFormattedLaunchDateString(launchDate);
  const rawUrl = isBrowser() ? window.location.href : '';
  const utf8Bytes = new TextEncoder().encode(rawUrl);
  const currentUrlInBase64 = Buffer.from(utf8Bytes).toString('base64');
  const adName = adList ? adList.map(ad => ad.name).join(' & ') : '';

  const carAlertModalContent: CarAlertModalConent = {
    carInfo: {
      id,
      make,
      model,
    },
    carAlert,
  };
  const adAlertModalContent: AdAlertModalConent = {
    adInfo: {
      id: adList ? adList[0]?.id : 0,
      name: adList ? adName : '',
      brand: make,
    },
    adAlert,
  };

  const pricesList = submodelList
    .filter(({ price }) => price !== null)
    .map(({ price }) => ((isPiModel && price !== null)
      ? Math.ceil(price / 100) * 100
      : price));

  const lowestPrice = pricesList[0];
  const highestPrice = pricesList[pricesList.length - 1];

  const mobilePriceText = getPriceRangeText(
    { lowest: lowestPrice, highest: highestPrice },
  );

  const withoutCoeLabel = submodelList.some(({ withCoe }) => !withCoe);

  const handleShortlistClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    const cookId = Cookies.get(COOKIE_KEY_SESSION_ID);
    const shortlist = Cookies.get(COOKIE_KEY_NON_LOGIN_SHORTLIST);

    if (cookId) {
      setIsAlertModalOpen(true);
    } else if (isDesktopMode) {
      window.open(
        joinSgcmMsSite(`/member/login?referrer=${currentUrlInBase64}`),
        '_self',
        'noreferrer noopener',
      );
    } else if (isShortlisted) {
      setIsShortlisted(false);
      Cookies.set(
        COOKIE_KEY_NON_LOGIN_SHORTLIST,
        shortlist
          ? shortlist.split(',')
            .filter(shortlistedId => shortlistedId !== id.toString())
            .join(',')
          : '',
        {
          domain: process.env.NODE_ENV !== 'development'
            ? SGCM_SITE_DOMAIN
            : undefined,
        },
      );
    } else if (shortlist) {
      if (shortlist.split(',').length >= NUM_NON_LOGIN_SHORTLIST_MAX_VEHICLES) {
        setIsWarningModalOpen(true);
      } else {
        Cookies.set(
          COOKIE_KEY_NON_LOGIN_SHORTLIST,
          `${shortlist},${id}`,
          {
            domain: process.env.NODE_ENV !== 'development'
              ? SGCM_SITE_DOMAIN
              : undefined,
          },
        );
        setIsShortlisted(true);
      }
    } else {
      Cookies.set(
        COOKIE_KEY_NON_LOGIN_SHORTLIST,
        id.toString(),
        {
          domain: process.env.NODE_ENV !== 'development'
            ? SGCM_SITE_DOMAIN
            : undefined,
        },
      );
      setIsShortlisted(true);
    }
  };

  const handleAlertModalSubmit = (
    alerts: CamelCaseAdAlert | CamelCaseCarAlert,
  ) => {
    onAlertModalSubmit?.(alerts);
  };

  const handleCheckboxChange = (submodelId: number, index: number) => {
    onComparedCheckboxChange?.({
      id,
      submodelId,
      make,
      model,
      name: submodelList[index].name,
      thumbnailUrl: `https://i.i-sgcm.com/new_cars/cars/${id}/${id}_m.jpg`,
    }, !isComparedSubmodelArray[index]);
  };

  const handleCarCardClick = () => {
    const destination = isDesktopMode
      ? joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${id}`).toString()
      : joinSgcmMobileSite(`/new_cars/overview.php?CarCode=${id}`).toString();

    window.open(
      destination,
      '_self',
      'noreferrer noopener',
    );
  };

  useEffect(() => {
    const toggleFilterModal = () => {
      if (window.location.hash === '') {
        setIsPromotionDetailsModalOpen(false);
      }
    };

    window.addEventListener('hashchange', toggleFilterModal);
  }, []);

  useEffect(() => {
    setIsShortlisted((userContext?.userId
      ? (carAlert.adCarPriceChange || carAlert.piCarPromo)
      : Cookies.get(COOKIE_KEY_NON_LOGIN_SHORTLIST)?.split(',')
        .includes(id.toString()) ?? false));
  }, [carAlert, id, userContext?.userId]);

  const activePromotionDetailsItem = promotionList
    ? promotionList.find(promotion => promotion.models.some(
      promoModel => promoModel.id === activePromotionListPromoId,
    )) as CamelCasePromotion
    : null;

  useBodyScrollLock({ isLocked: isWarningModalOpen });

  return (
    <div
      className={styles.containerRoot}>
      <Modal
        className={cx(
          styles.containerModal,
          styles.isSmall,
        )}
        isOpen={isWarningModalOpen}
        title={null}
        isBackdropStatic
        onClose={() => setIsWarningModalOpen(false)}>
        <Result
          className={styles.containerResult}
          message={(
            <>
              <span className={styles.textWarningMessage}>
                You have reached the maximum of
                {' '}
                {NUM_NON_LOGIN_SHORTLIST_MAX_VEHICLES}
                {' '}
                vehicles.
              </span>
              <span className={cx(
                styles.textWarningMessage,
                styles.isNote,
              )}>
                Please
                {' '}
                <Link href={`${joinSgcmSite('/member/login')}?referrer=${currentUrlInBase64}`}>login</Link>
                {' '}
                to shortlist more.
              </span>
            </>
          )}
          title="Limit Reached"
          variant={RESULT_VARIANT_WARNING} />
      </Modal>
      <DepreciationModal
        isOpen={isDepreciationModalOpen}
        onClose={() => {
          setIsDepreciationModalOpen(false);
        }} />
      <AlertModal
        adAlertModalContent={adAlertModalContent}
        carAlertModalContent={carAlertModalContent}
        isOpen={isAlertModalOpen}
        type={isPiModel ? 'piAlert' : 'carAlert'}
        user={user}
        onClose={() => {
          setIsAlertModalOpen(false);
        }}
        onSubmit={handleAlertModalSubmit} />
      {isDesktopMode
      && (
        <>
          <div className={styles.containerLeftColumn}>
            <a
              className={styles.containerVehicleImage}
              href={`${joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${id}`)}`}
              rel="noreferrer noopener">
              <ImageWithFallback
                alt={`${vehicleFullName} image`}
                className={styles.imageVehicle}
                height={111}
                priority={isPriority}
                src={thumbnailUrl}
                width={167} />
              <div className={styles.containerimageIcons}>
                {has360 && (
                  <Image
                    alt="has panorama"
                    className={styles.hasPanorama}
                    height={30}
                    src={`${joinSgcmMedia('/public/new-cars/icon/Panorama.svg')}`}
                    width={30} />
                )}
                {hasVideo && (
                  <Image
                    alt="has video"
                    className={styles.hasVideo}
                    height={30}
                    src={`${joinSgcmMedia('/public/new-cars/icon/Video.svg')}`}
                    width={30} />
                )}
              </div>
            </a>
            <div className={styles.containerModelLabel}>
              {isNewModel && (
                <ListTypeLabel
                  labelSize={LIST_TYPE_LABEL_SIZE}
                  type="newModel" />
              )}
              {isSpotlight && (
                <ListTypeLabel
                  labelSize={LIST_TYPE_LABEL_SIZE}
                  type="spotlight" />
              )}
              {isUpcoming && (
                <ListTypeLabel
                  labelSize={LIST_TYPE_LABEL_SIZE}
                  type="upcoming" />
              )}
              {hasPromotion && (
                <button
                  aria-label="Open promotion details"
                  onClick={() => {
                    setActivePromotionListPromoId(id);
                    setIsPromotionDetailsModalOpen(true);
                  }}>
                  <div className={styles.textPromoDeals}>Promo Deals</div>
                </button>
              )}
            </div>
          </div>
          <div className={styles.containerRightColumn}>
            <div className={styles.containerContentTable}>
              <div className={cx(
                styles.textModelName,
                styles.containerTableHeader,
              )}>
                <a
                  href={`${joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${id}`)}`}
                  rel="noreferrer noopener">
                  {vehicleFullName}
                </a>
              </div>
              <div className={styles.containerTableHeader}>
                {country}
              </div>
              <div className={styles.containerShortlistCell}>
                {count && (
                  <div className={styles.containerTableHeader}>
                    <Link
                      className={styles.containerStarRating}
                      href={`${joinSgcmSite(`/new_cars/newcars_reviews.php?CarCode=${id}`)}`}>
                      <StarRating
                        iconSize={STAR_RATING_ICON_SIZE_DEFAULT}
                        value={ratingValue ?? 0} />
                      <div>
                        <span className={styles.textVehicleRatingCount}>
                          (
                          {count}
                          )
                        </span>
                      </div>
                    </Link>
                  </div>
                )}
                <button onClick={handleShortlistClick}>
                  <Image
                    alt={isShortlisted
                      ? 'containedShortlistIcon'
                      : 'outlinedShortlistIcon'}
                    className={styles.imageVehicle}
                    height={17}
                    src={
                      isShortlisted
                        ? `${joinSgcmMedia('/public/new-cars/icon/HeartContained.svg')}`
                        : `${joinSgcmMedia('/public/new-cars/icon/HeartOutlined.svg')}`
                    }
                    width={18.98} />
                </button>
              </div>
              {!isUpcoming && (
                <>
                  {submodelList.map(({
                    id: submodelId,
                    name,
                    price,
                    priceFluctuation,
                    depreciation,
                    power,
                    fuelEconomy,
                    transmission,
                    transmissionType,
                    isFacelift,
                    faceliftReviewUrl,
                    isNewVariant,
                    withCoe,
                  }, idx) => {
                    const submodelPrice = ((isPiModel && price !== null)
                      ? Math.ceil(price / 100) * 100
                      : price);
                    const checkboxId = `checkbox-${submodelId}`;
                    const submodelName = `${name} (${transmissionType})`;
                    const powerText = power ? `${power}bhp` : '';
                    const spec = [fuelEconomy, powerText, transmission]
                      .filter(
                        Boolean,
                      ).join(' • ');
                    const isPricePremium = (
                      priceFluctuation?.amount ?? 0
                    ) > 0;
                    const links = [
                      { href: `${joinSgcmSite(`/new_cars/newcars_specs.php?CarCode=${id}&Subcode=${submodelId}`)}`, label: 'Specs' },
                      { href: `${joinSgcmSite(`/new_cars/newcars_features.php?CarCode=${id}&Subcode=${submodelId}`)}`, label: 'Features' },
                    ];

                    if (!adList) {
                      links.push({ href: `${joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${id}&Subcode=${submodelId}`)}`, label: 'Pricelist' });
                    }
                    return (
                      <Fragment key={submodelId}>
                        <div className={styles.containerSubmodelName}>
                          <div className={styles.containerTooltipRoot}>
                            <div
                              className={styles.containerSubmodelCheck}>
                              <Checkbox
                                checked={isComparedSubmodelArray[idx]}
                                id={checkboxId}
                                size={SHORTLIST_CHECKBOX_SIZE}
                                value={id}
                                onChange={
                                  () => handleCheckboxChange(submodelId, idx)
                                } />
                              <label
                                className={styles.textLabel}
                                htmlFor={name}>
                                {submodelName}
                              </label>
                              {isFacelift && (
                                <ListTypeLabel
                                  labelSize={LIST_TYPE_LABEL_SIZE}
                                  type="facelift" />
                              )}
                              {isNewVariant && (
                                <ListTypeLabel
                                  labelSize={LIST_TYPE_LABEL_SIZE}
                                  type="newSubmodel" />
                              )}
                              <div className={styles.containerTooltip}>
                                {links.map(({ href, label }, i) => (
                                  <div key={`tooltip-${label}`}>
                                    <a
                                      href={href}
                                      rel="noreferrer noopener">
                                      {label}
                                    </a>
                                    <span className={cx(
                                      styles.linkSeparate,
                                      i === links.length - 1 && styles.isLastLink, // @suppress longLineCheck
                                    )}>
                                      {i < links.length - 1 && '|'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          {faceliftReviewUrl !== null && (
                            <TextLink
                              className={styles.linkToFaceliftUrl}
                              href={faceliftReviewUrl}
                              text="Read facelift article" />
                          )}
                        </div>
                        <div className={styles.containerPrice}>
                          <div className={styles.textPrice}>
                            {submodelPrice ? ` $${submodelPrice.toLocaleString('en-US')}` : 'POA'}
                            {submodelPrice && priceFluctuation !== null
                            && (
                              <Tooltip
                                className={styles.priceFluctuationTooltip}
                                content={(
                                  <div className={
                                    styles.containerPriceFluctuationTooltip
                                  }>
                                    <div>
                                      {`Price ${priceFluctuation?.amount > 0 ? 'Increased' : 'Decreased'} by $ ${Math.abs(priceFluctuation?.amount).toLocaleString('en-US')} on ${getFormattedDateString(priceFluctuation?.date, 'shorten')}`}
                                      <br />
                                      <a
                                        className={
                                          styles.containerSubmodelTooltipPricelistLink // @suppress longLineCheck
                                        }
                                        href={joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${id}&Subcode=${submodelId}`).toString()}>
                                        View full price history
                                        <IosArrowRight
                                          className={styles.iconInTooltip}
                                          size={ARROW_ICON_SIZE_IN_TOOLTIP} />
                                      </a>
                                    </div>
                                  </div>
                                )}>
                                <div>
                                  <Image
                                    alt={isPricePremium
                                      ? 'redArrowIcon'
                                      : 'greenArrowIcon'}
                                    className={styles.imageArrowIcon}
                                    height={18}
                                    src={
                                      isPricePremium
                                        ? `${joinSgcmMedia('/public/new-cars/icon/redArrow.svg')}`
                                        : `${joinSgcmMedia('/public/new-cars/icon/greenArrow.svg')}`
                                    }
                                    width={11} />
                                </div>
                              </Tooltip>
                            )}
                          </div>
                          <div className={styles.textDepreciation}>
                            {submodelPrice && !withCoe && <span>w/o COE</span>}
                            {depreciation && withCoe
                            && (
                              <>
                                $
                                {depreciation.toLocaleString('en-US')}
                                {' '}
                                /yr
                                <button
                                  onClick={
                                    () => setIsDepreciationModalOpen(true)
                                  }>
                                  <Image
                                    alt="depreciationInfo"
                                    className={styles.imageVehicle}
                                    height={18}
                                    src={`${joinSgcmMedia('/public/new-cars/icon/information.svg')}`}
                                    width={11} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className={styles.textSpecDetail}>
                          {spec}
                        </div>
                      </Fragment>
                    );
                  })}
                </>
              )}
              {isUpcoming && (
                <div className={styles.textUpcoming}>
                  {`Expected launch date is ${formattedLaunchDate}`}
                </div>
              )}
            </div>
            {!isUpcoming && (
              <div className={cx(
                styles.containerFooterRow,
                !cotyDescription && !isCotyNominee && styles.hasSingleItem,
              )}>
                {cotyDescription && (
                  <a
                    href={joinSgcmSite('/coty2024/winners.php').toString()}
                    rel="noreferrer"
                    target="_blank">
                    <div className={styles.textCotyDescription}>
                      <Image
                        alt="car of the year"
                        height={19}
                        src="https://i.i-sgcm.com/new_cars/desktop_listing_info_coty.svg"
                        width={30} />
                      <div>
                        {cotyDescription}
                      </div>
                    </div>
                  </a>
                )}
                {isCotyNominee && (
                  <a
                    href={`${joinSgcmSite('/coty2024/vote.php')}`}
                    rel="noreferrer noopener"
                    target="_blank">
                    <div className={styles.cotyNomineeTagContainer}>
                      <div className={cx(
                        styles.tagButton,
                        styles.leftTagButton,
                      )}>
                        COTY NOMINEE
                      </div>
                      <div className={cx(
                        styles.tagButton,
                        styles.rightTagButton,
                      )}>
                        VOTE FOR ME
                        <IosArrowRight
                          size={ARROW_ICON_SIZE} />
                      </div>
                    </div>
                  </a>
                )}
                <div className={styles.adContainer}>
                  {adList && adList.map(({ id: dealerId, name }, index) => (
                    <div
                      key={`ad-${dealerId}`}>
                      <TextLink
                        href={`${joinSgcmMsSite(SITE_BASE_PATH ?? '')}/listing/search?q=${make}&category=ad&dealer_id=${dealerId}`}
                        text={name} />
                      {index < adList.length - 1 && (
                        <span className={styles.textSeperator}>|</span>
                      )}
                    </div>
                  ))}
                  {isPiModel
                  && (
                    isDesktopMode
                      ? (
                        <TextLink
                          href={`${joinSgcmSite(`/new_cars/newcars_pricing.php?CarCode=${id}`)}`}
                          text="Parallel Importers" />
                      )
                      : (
                        <TextLink
                          href={`${joinSgcmMobileSite(`/new_cars/overview.php?CarCode=${id}`)}#dealers-selling-vehicle`}
                          text="Parallel Importers" />
                      )
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {!isDesktopMode
      && (
        <div
          className={styles.containerMobileCard}
          role="button"
          tabIndex={0}
          onClick={handleCarCardClick}
          onKeyDown={handleCarCardClick}>
          <div className={styles.containerMobileHeader}>
            <div className={cx(
              styles.textModelName,
              styles.containerTableHeader,
            )}>
              <a
                href={isDesktopMode
                  ? `${joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${id}`)}`
                  : `${joinSgcmMobileSite(`/new_cars/overview.php?CarCode=${id}`)}`}
                rel="noreferrer noopener">
                {vehicleFullName}
              </a>
            </div>
            <button onClick={handleShortlistClick}>
              <Image
                alt={isShortlisted
                  ? 'containedShortlistIcon'
                  : 'outlinedShortlistIcon'}
                className={styles.imageVehicle}
                height={17}
                src={
                  isShortlisted
                    ? `${joinSgcmMedia('/public/new-cars/icon/HeartContained.svg')}`
                    : `${joinSgcmMedia('/public/new-cars/icon/HeartOutlined.svg')}`
                }
                width={18.98} />
            </button>
          </div>
          <div className={styles.containerContent}>
            <div className={styles.containerLeftColumn}>
              <a
                className={styles.containerVehicleImage}
                href={isDesktopMode
                  ? `${joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${id}`)}`
                  : `${joinSgcmMobileSite(`/new_cars/overview.php?CarCode=${id}`)}`}
                rel="noreferrer noopener">
                <ImageWithFallback
                  alt={`${vehicleFullName} image`}
                  className={styles.imageVehicle}
                  height={87}
                  priority={isPriority}
                  src={thumbnailUrl}
                  width={130} />
                <div className={styles.containerimageIcons}>
                  {has360 && (
                    <Image
                      alt="has panorama"
                      className={styles.hasPanorama}
                      height={20}
                      src={`${joinSgcmMedia('/public/new-cars/icon/Panorama.svg')}`}
                      width={20} />
                  )}
                  {hasVideo && (
                    <Image
                      alt="has video"
                      className={styles.hasVideo}
                      height={20}
                      src={`${joinSgcmMedia('/public/new-cars/icon/Video.svg')}`}
                      width={20} />
                  )}
                </div>
              </a>
              <div className={styles.containerModelLabel}>
                {isNewModel && (
                  <ListTypeLabel
                    labelSize={LIST_TYPE_LABEL_SIZE}
                    type="newModel" />
                )}
                {isUpcoming && (
                  <ListTypeLabel
                    labelSize={LIST_TYPE_LABEL_SIZE}
                    type="upcoming" />
                )}
                {hasPromotion && (
                  <TextLink
                    className={styles.textPromoLink}
                    href={isDesktopMode
                      ? `${joinSgcmSite(`/new_cars/newcars_promo.php?CarCode=${id}`)}`
                      : `${joinSgcmMobileSite(`/new_cars/promo.php?CarCode=${id}`)}`}
                    text="Promo"
                    onClick={e => e.stopPropagation()} />
                )}
              </div>
            </div>
            <div className={styles.containerRightColumn}>
              <div className={cx(
                styles.textPrice,
                styles.textMobilePrice,
              )}>
                {isUpcoming
                  ? 'TBA'
                  : (
                    <>
                      {mobilePriceText}
                      {' '}
                      {withoutCoeLabel && (
                        <span className={styles.textCoeInMobileTitle}>
                          (w/o COE)
                        </span>
                      )}
                    </>
                  )}
              </div>
              {count && (
                <div className={styles.containerTableHeader}>
                  <div className={styles.containerStarRating}>
                    <StarRating
                      iconSize={STAR_RATING_ICON_SIZE_DEFAULT}
                      value={ratingValue ?? 0} />
                    <Link
                      className={styles.textVehicleRatingCount}
                      href={isDesktopMode
                        ? `${joinSgcmSite(`/new_cars/newcars_reviews.php?CarCode=${id}`)}`
                        : `${joinSgcmMobileSite(`/new_cars/review.php?CarCode=${id}`)}`}
                      onClick={e => e.stopPropagation()}>
                      (
                      {count}
                      )
                    </Link>
                  </div>
                </div>
              )}
              <div className={styles.textCountry}>
                {country}
              </div>
              <div className={styles.adContainer}>
                {adList && adList.map(({ id: dealerId, name }, index) => (
                  <div
                    key={`ad-${dealerId}`}>
                    <TextLink
                      href={`${joinSgcmMsSite(SITE_BASE_PATH ?? '')}/listing/search?q=${make}&category=ad&dealer_id=${dealerId}`}
                      text={name}
                      onClick={e => e.stopPropagation()} />
                    {index < adList.length - 1 && (
                      <span className={styles.textSeperator}>|</span>
                    )}
                  </div>
                ))}
                {isPiModel
                && (
                  <TextLink
                    href={isDesktopMode
                      ? `${joinSgcmSite(`/new_cars/newcars_pricing.php?CarCode=${id}`)}`
                      : `${joinSgcmMobileSite(`/new_cars/overview.php?CarCode=${id}`)}#dealers-selling-vehicle`}
                    text="Parallel Importers"
                    onClick={e => e.stopPropagation()} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <PromotionDetailsModal
        key={promotionDetailsModalKey}
        isOpen={isPromotionDetailsModalOpen}
        promotion={activePromotionDetailsItem}
        onClose={() => {
          setIsPromotionDetailsModalOpen(false);
          window.history.pushState(null, '', ' ');
        }}
        onExit={() => {
          setPromotionDetailsModalKey(createTimestampUid());
        }} />
    </div>
  );
};

export default CarCard;
